import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import FooterComponent from "../navigation/footer/footerComponent";
import { createApplication, updateApplication } from "../../models/firestoreService";
import { sendSlackMessageTracked, sendApplicationConfirmation } from "../../models/notifications";

const PAGE_URL = "https://externacademy.com/apply";
const OG_IMAGE = "https://firebasestorage.googleapis.com/v0/b/extern-academy.firebasestorage.app/o/website%2Fimages%2Fcustom%2Fplanning.webp?alt=media&token=a149cc36-388a-425c-b253-06da7a1438e8";

const COHORTS = ["2027 Startup Sprint", "Founder Track", "Residency"];
const INTERESTS = [
    "Software Development",
    "Data Science / Analytics / AI",
    "Marketing / Communications",
    "Finance / Accounting",
    "Design / UX / UI",
    "Business Operations / Logistics",
    "Other",
];
const GRADES = ["9th Grade", "10th Grade", "11th Grade", "12th Grade", "College Freshman", "Other"];

// ── Validation ────────────────────────────────────────────────────────────────

function validateS1(d) {
    const errs = [];
    if (!d.fullName.trim()) errs.push("Full Legal Name is required");
    if (!d.preferredName.trim()) errs.push("First / Preferred Name is required");
    if (!d.email.trim() || !/\S+@\S+\.\S+/.test(d.email)) errs.push("A valid Email Address is required");
    if (!d.phone.trim()) errs.push("Phone Number is required");
    if (!d.dob) errs.push("Date of Birth is required");
    if (!d.institution.trim()) errs.push("Current Academic Institution is required");
    if (!d.gpa.trim()) errs.push("GPA is required");
    if (!d.gradDate) errs.push("Expected Graduation Date is required");
    if (!d.gradeLevel) errs.push("Current Grade Level is required");
    if (!d.guardianName.trim()) errs.push("Parent / Guardian's Full Name is required");
    if (!d.guardianEmail.trim() || !/\S+@\S+\.\S+/.test(d.guardianEmail)) errs.push("A valid Parent / Guardian Email is required");
    return errs;
}

function validateS2(d) {
    const errs = [];
    if (d.cohort.length === 0) errs.push("Please select at least one cohort");
    if (d.interests.length === 0) errs.push("Please select at least one area of interest");
    return errs;
}

function validateS3(d) {
    const errs = [];
    if (!d.programFit.trim()) errs.push("Please tell us why you're a strong fit");
    if (!d.technicalSkills.trim()) errs.push("Technical Skills & Proficiencies is required");
    if (!d.novelProblem.trim()) errs.push("Novel problem example is required");
    if (!d.outlier.trim()) errs.push("Please tell us what makes you an outlier");
    if (!d.portfolioUrl.trim()) errs.push("Portfolio or GitHub URL is required");
    return errs;
}

const ESSAY_PROMPTS = [
    {
        id: "initiative",
        prompt: "Tell us about something you wanted to make happen — and what you actually did about it.",
        hint: "Gets at initiative and follow-through, not just ideas.",
    },
    {
        id: "problem",
        prompt: "Describe a problem you've noticed that you wish someone would fix. Why does it bother you, and how would you start?",
        hint: "Reveals how you spot problems and whether you think in solutions.",
    },
    {
        id: "built",
        prompt: "Write about a time you built, made, or started something from scratch — however big or small. What did you learn?",
        hint: "Surfaces hands-on experience and reflection, no matter your background.",
    },
    {
        id: "ambition",
        prompt: "What would you build if you knew you couldn't fail? Now tell us the first real step you'd take toward it anyway.",
        hint: "Tests ambition and grounds it in action — dreamers vs. doers.",
    },
    {
        id: "resilience",
        prompt: "Tell us about a time something you tried didn't work. What did you do next?",
        hint: "Gets at grit and resilience — how you handle things going wrong.",
    },
];

function validateS4(d) {
    const errs = [];
    if (!d.essayPrompt) errs.push("Please select an essay prompt");
    if (!d.essay.trim()) errs.push("Essay response is required");
    if (!d.videoUrl.trim()) errs.push("Video link is required");
    return errs;
}

// ── Shared UI primitives ──────────────────────────────────────────────────────

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 bg-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors";

function Field({ label, required, children }) {
    return (
        <div className="mb-5">
            <label className="block text-md font-medium text-gray-600 mb-1.5" style={{fontSize: "20px"}}>
                {label}{required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {children}
        </div>
    );
}

function Errors({ errors }) {
    if (!errors.length) return null;
    return (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
            <ul className="list-disc list-inside space-y-1">
                {errors.map((e, i) => <li key={i} className="text-sm text-red-600">{e}</li>)}
            </ul>
        </div>
    );
}

function NavButtons({ onBack, onNext, nextLabel = "Next →", saving }) {
    return (
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">
            {onBack
                ? <button onClick={onBack} className="px-5 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">← Back</button>
                : <div />
            }
            <button
                onClick={onNext}
                disabled={saving}
                className="px-7 py-2.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
                style={{ backgroundColor: "#3769B9" }}
            >
                {saving ? "Saving…" : nextLabel}
            </button>
        </div>
    );
}

function CohortCards({ selected, onChange }) {
    const toggle = (opt) =>
        onChange(selected.includes(opt) ? selected.filter(o => o !== opt) : [...selected, opt]);
    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {COHORTS.map(opt => {
                const active = selected.includes(opt);
                return (
                    <button
                        key={opt}
                        type="button"
                        onClick={() => toggle(opt)}
                        className="relative rounded-xl px-4 py-4 text-left transition-all border-2"
                        style={{
                            borderColor: active ? "#3769B9" : "#e5e7eb",
                            backgroundColor: active ? "#f0f4fb" : "#fff",
                        }}
                    >
                        <div
                            className="w-4 h-4 rounded-full border-2 mb-3 flex items-center justify-center"
                            style={{ borderColor: active ? "#3769B9" : "#d1d5db" }}
                        >
                            {active && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "#3769B9" }} />}
                        </div>
                        <span className="text-sm font-semibold" style={{ color: active ? "#3769B9" : "#374151" }}>
                            {opt}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

function InterestPills({ selected, onChange }) {
    const toggle = (opt) =>
        onChange(selected.includes(opt) ? selected.filter(o => o !== opt) : [...selected, opt]);
    return (
        <div className="flex flex-wrap gap-2">
            {INTERESTS.map(opt => {
                const active = selected.includes(opt);
                return (
                    <button
                        key={opt}
                        type="button"
                        onClick={() => toggle(opt)}
                        className="px-4 py-2 rounded-full text-sm font-medium transition-all border"
                        style={{
                            borderColor: active ? "#3769B9" : "#e5e7eb",
                            backgroundColor: active ? "#3769B9" : "#fff",
                            color: active ? "#fff" : "#6b7280",
                        }}
                    >
                        {opt}
                    </button>
                );
            })}
        </div>
    );
}

// ── Progress bar ──────────────────────────────────────────────────────────────

function ProgressBar({ step }) {
    const steps = ["Personal Info", "Program", "Essays", "Finish"];
    return (
        <div className="flex items-start mb-10">
            {steps.map((label, i) => {
                const n = i + 1;
                const done = step > n;
                const active = step === n;
                return (
                    <div key={n} className="flex items-start flex-1">
                        <div className="flex flex-col items-center w-full">
                            <div
                                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                                    done ? "bg-green-500 text-white" :
                                    active ? "text-white" : "bg-gray-100 text-gray-400"
                                }`}
                                style={active ? { backgroundColor: "#3769B9" } : {}}
                            >
                                {done ? "✓" : n}
                            </div>
                            <span
                                className={`text-xs mt-1.5 text-center hidden sm:block ${active ? "font-semibold" : "text-gray-400"}`}
                                style={active ? { color: "#3769B9" } : {}}
                            >
                                {label}
                            </span>
                        </div>
                        {i < steps.length - 1 && (
                            <div className={`flex-1 h-px mt-4 mx-1 ${step > n ? "bg-green-400" : "bg-gray-200"}`} />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ── Section 1: Personal Info ──────────────────────────────────────────────────

function Section1({ data, set, onNext, saving }) {
    const [errors, setErrors] = useState([]);
    const f = (key) => (e) => set(prev => ({ ...prev, [key]: e.target.value }));

    const handleNext = () => {
        const errs = validateS1(data);
        if (errs.length) { setErrors(errs); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
        setErrors([]);
        onNext();
    };

    return (
        <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1" style={{fontSize: "24px"}}>Extern Academy Candidate Application</h2>
            <p className="text-sm text-gray-500 mb-7 leading-relaxed">
                Over the course of your externship, you'll be embedded onto a mini-startup team to build a live product
                in tech, design, or marketing. This application is your first step toward transforming theoretical
                knowledge into tangible results and beginning your professional story far ahead of the curve.
            </p>
            <Errors errors={errors} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5">
                <div className="sm:col-span-2">
                    <Field label="Full Legal Name" required>
                        <input className={inputCls} value={data.fullName} onChange={f("fullName")} placeholder="As it appears on official documents" />
                    </Field>
                </div>
                <Field label="First / Preferred Name" required>
                    <input className={inputCls} value={data.preferredName} onChange={f("preferredName")} placeholder="What you go by" />
                </Field>
                <Field label="Email Address" required>
                    <input className={inputCls} type="email" value={data.email} onChange={f("email")} placeholder="you@example.com" />
                </Field>
                <Field label="Phone Number" required>
                    <input className={inputCls} type="tel" value={data.phone} onChange={f("phone")} placeholder="+1 (___) ___-____" />
                </Field>
                <Field label="Date of Birth" required>
                    <input className={inputCls} type="date" value={data.dob} onChange={f("dob")} />
                </Field>
                <div className="sm:col-span-2">
                    <Field label="Current Academic Institution" required>
                        <input className={inputCls} value={data.institution} onChange={f("institution")} placeholder="School or university name" />
                    </Field>
                </div>
                <Field label="GPA" required>
                    <input className={inputCls} value={data.gpa} onChange={f("gpa")} placeholder="e.g. 3.8" />
                </Field>
                <Field label="Current Grade Level" required>
                    <select className={inputCls} value={data.gradeLevel} onChange={f("gradeLevel")}>
                        <option value="">Select grade…</option>
                        {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                </Field>
                <div className="sm:col-span-2">
                    <Field label="Expected Graduation Date" required>
                        <input className={inputCls} type="date" value={data.gradDate} onChange={f("gradDate")} />
                    </Field>
                </div>
                <Field label="Parent / Guardian's Full Name" required>
                    <input className={inputCls} value={data.guardianName} onChange={f("guardianName")} placeholder="Full name" />
                </Field>
                <Field label="Parent / Guardian's Email Address" required>
                    <input className={inputCls} type="email" value={data.guardianEmail} onChange={f("guardianEmail")} placeholder="parent@example.com" />
                </Field>
            </div>
            <NavButtons onNext={handleNext} saving={saving} />
        </div>
    );
}

// ── Section 2: Program Selection ──────────────────────────────────────────────

function Section2({ data, set, onNext, onBack, saving }) {
    const [errors, setErrors] = useState([]);

    const handleNext = () => {
        const errs = validateS2(data);
        if (errs.length) { setErrors(errs); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
        setErrors([]);
        onNext();
    };

    return (
        <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Program Selection</h2>
            <p className="text-sm text-gray-500 mb-7">Tell us which program you're targeting and what drives you.</p>
            <Errors errors={errors} />
            <Field label="Which cohort are you applying for?" required>
                <CohortCards
                    selected={data.cohort}
                    onChange={(v) => set(prev => ({ ...prev, cohort: v }))}
                />
            </Field>
            <Field label="Select Your Interests" required>
                <InterestPills
                    selected={data.interests}
                    onChange={(v) => set(prev => ({ ...prev, interests: v }))}
                />
            </Field>
            <NavButtons onBack={onBack} onNext={handleNext} saving={saving} />
        </div>
    );
}

// ── Section 3: Essays ─────────────────────────────────────────────────────────

function Section3({ data, set, onNext, onBack, saving }) {
    const [errors, setErrors] = useState([]);
    const f = (key) => (e) => set(prev => ({ ...prev, [key]: e.target.value }));

    const handleNext = () => {
        const errs = validateS3(data);
        if (errs.length) { setErrors(errs); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
        setErrors([]);
        onNext();
    };

    return (
        <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Tell Us About Yourself</h2>
            <p className="text-sm text-gray-500 mb-7">This is your opportunity to stand out. Be specific and genuine.</p>
            <Errors errors={errors} />
            <Field label="Why are you a strong fit for this program?" required>
                <p className="text-xs text-gray-400 mb-2">Tell us about your unique skills, experiences, or passions that align with this program. Consider your past projects, relevant coursework, or personal drive.</p>
                <textarea className={`${inputCls} resize-none`} rows={5} value={data.programFit} onChange={f("programFit")} placeholder="Tell us about your unique skills, experiences, or passions that align with this program." />
            </Field>
            <Field label="Technical Skills & Proficiencies" required>
                <textarea className={`${inputCls} resize-none`} rows={4} value={data.technicalSkills} onChange={f("technicalSkills")} placeholder="List relevant technical skills." />
            </Field>
            <Field label="Please share an example of a time you tackled a problem in a novel way and achieved something truly impressive." required>
                <textarea className={`${inputCls} resize-none`} rows={5} value={data.novelProblem} onChange={f("novelProblem")} placeholder="Describe the situation, your approach, and the result." />
            </Field>
            <Field label="What makes you an outlier?" required>
                <textarea className={`${inputCls} resize-none`} rows={4} value={data.outlier} onChange={f("outlier")} placeholder="Share what sets you apart." />
            </Field>
            <Field label="Portfolio or GitHub Profile" required>
                <input className={inputCls} type="url" value={data.portfolioUrl} onChange={f("portfolioUrl")} placeholder="Paste your link here (GitHub is optional)" />
            </Field>
            <NavButtons onBack={onBack} onNext={handleNext} saving={saving} />
        </div>
    );
}

// ── Section 4: Essay & Video ──────────────────────────────────────────────────

function Section4({ data, set, onSubmit, onBack, saving, submitted }) {
    const [errors, setErrors] = useState([]);
    const f = (key) => (e) => set(prev => ({ ...prev, [key]: e.target.value }));

    const handleSubmit = () => {
        const errs = validateS4(data);
        if (errs.length) { setErrors(errs); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
        setErrors([]);
        onSubmit();
    };

    return (
        <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">{submitted ? "Review & Update" : "One Last Step"}</h2>
            <p className="text-sm text-gray-500 mb-8">{submitted ? "Make any changes below, then click Update Application to save them." : "Submit your essay and video to complete your application."}</p>
            <Errors errors={errors} />
            <Field label="Essay Prompt" required>
                <p className="text-xs text-gray-400 mb-3">Choose one prompt to respond to. Be specific and genuine — there's no right answer, only an honest one.</p>
                <div className="flex flex-col gap-3">
                    {ESSAY_PROMPTS.map(({ id, prompt, hint }) => {
                        const selected = data.essayPrompt === id;
                        return (
                            <button
                                key={id}
                                type="button"
                                onClick={() => set(prev => ({ ...prev, essayPrompt: id }))}
                                className="text-left rounded-lg px-4 py-3 transition-all"
                                style={{
                                    border: selected ? "2px solid #3769B9" : "1.5px solid #e5e7eb",
                                    backgroundColor: selected ? "#f0f5ff" : "#fff",
                                }}
                            >
                                <p className="text-sm font-semibold mb-0.5" style={{ color: selected ? "#3769B9" : "#111" }}>
                                    {prompt}
                                </p>
                                <p className="text-xs mb-0" style={{ color: "#888" }}>{hint}</p>
                            </button>
                        );
                    })}
                </div>
            </Field>
            <Field label="Your Response" required>
                <p className="text-xs text-gray-400 mb-2">No strict word count — but be thoughtful and specific. Vague answers don't stand out.</p>
                <textarea
                    className={`${inputCls} resize-none`}
                    rows={10}
                    value={data.essay}
                    onChange={f("essay")}
                    placeholder="Write your response here…"
                />
            </Field>
            <Field label="5-Minute Video Link" required>
                <p className="text-xs text-gray-400 mb-2">Record a short video introducing yourself — who you are, what you're building or thinking about, and why you're applying. Upload it to Loom, YouTube (unlisted), or Google Drive and paste the link below.</p>
                <input
                    className={inputCls}
                    type="url"
                    value={data.videoUrl}
                    onChange={f("videoUrl")}
                    placeholder="https://loom.com/share/… or YouTube / Drive link"
                />
            </Field>
            <NavButtons
                onBack={onBack}
                onNext={handleSubmit}
                nextLabel={saving ? (submitted ? "Updating…" : "Submitting…") : (submitted ? "Update Application" : "Submit Application")}
                saving={saving}
            />
        </div>
    );
}

// ── Submitted banner ──────────────────────────────────────────────────────────

function SubmittedBanner({ submittedAt, updated }) {
    return (
        <div
            className="rounded-xl px-5 py-4 mb-6 flex items-start gap-3"
            style={{ backgroundColor: updated ? "#f0fdf4" : "#f0f4fb", border: `1px solid ${updated ? "#86efac" : "#d0dff5"}`, transition: "all 0.4s" }}
        >
            <span className="text-lg mt-0.5">{updated ? "✓" : "📋"}</span>
            <div>
                <p className="text-sm font-semibold text-gray-800">
                    {updated ? "Application updated!" : "Application submitted"}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                    {updated
                        ? "Your changes have been saved."
                        : `Submitted${submittedAt ? ` on ${new Date(submittedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}` : ""}. You can edit and re-submit any time below.`
                    }
                </p>
            </div>
        </div>
    );
}

// ── localStorage helpers ──────────────────────────────────────────────────────

const LS_KEY = "extern_apply_draft";

function loadDraft() {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) ?? {}; } catch { return {}; }
}

function saveDraft(patch) {
    try {
        const current = loadDraft();
        localStorage.setItem(LS_KEY, JSON.stringify({ ...current, ...patch }));
    } catch {}
}

function clearDraft() {
    try { localStorage.removeItem(LS_KEY); } catch {}
}

// ── Main component ────────────────────────────────────────────────────────────

export default function MainApplyComponent() {
    const draft = loadDraft();

    const [step, setStep] = useState(draft.step ?? 1);
    const [recordId, setRecordId] = useState(draft.recordId ?? null);
    const [submitted, setSubmitted] = useState(draft.submitted ?? false);
    const [submittedAt, setSubmittedAt] = useState(draft.submittedAt ?? null);
    const [updated, setUpdated] = useState(false);
    const [saving, setSaving] = useState(false);

    const [s1, setS1] = useState({
        fullName: "", preferredName: "", email: "", phone: "",
        dob: "", institution: "", gpa: "", gradDate: "",
        gradeLevel: "", guardianName: "", guardianEmail: "",
        ...draft.s1,
    });
    const [s2, setS2] = useState({ cohort: [], interests: [], ...draft.s2 });
    const [s3, setS3] = useState({
        programFit: "", technicalSkills: "", novelProblem: "", outlier: "", portfolioUrl: "",
        ...draft.s3,
    });
    const [s4, setS4] = useState({ essayPrompt: "", essay: "", videoUrl: "", ...draft.s4 });

    useEffect(() => { saveDraft({ step, recordId, submitted, submittedAt, s1, s2, s3, s4 }); }, [step, recordId, submitted, submittedAt, s1, s2, s3, s4]);

    const s1Fields = () => ({
        "Full Legal Name": s1.fullName,
        "Preferred Name": s1.preferredName,
        "Email": s1.email,
        "Phone": s1.phone,
        "Date of Birth": s1.dob,
        "Academic Institution": s1.institution,
        "GPA": s1.gpa,
        "Expected Graduation": s1.gradDate,
        "Grade Level": s1.gradeLevel,
        "Guardian Name": s1.guardianName,
        "Guardian Email": s1.guardianEmail,
    });

    const handleSection1 = async () => {
        setSaving(true);
        try {
            if (recordId) {
                await updateApplication(recordId, { ...s1Fields(), "Status": submitted ? "Complete" : "Section 1" });
            } else {
                const id = await createApplication({ ...s1Fields(), "Status": "Section 1" });
                setRecordId(id);
                sendSlackMessageTracked(`New application started — ${s1.preferredName || s1.fullName} (${s1.email})`);
            }
        } catch (e) {
            console.error("Airtable save failed:", e);
        }
        setSaving(false);
        setStep(2);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleSection2 = async () => {
        setSaving(true);
        try {
            if (recordId) {
                await updateApplication(recordId, {
                    "Cohort": s2.cohort,
                    "Interests": s2.interests,
                    "Status": "Section 2",
                });
            }
        } catch (e) {
            console.error("Airtable update failed:", e);
        }
        setSaving(false);
        setStep(3);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleSection3 = async () => {
        setSaving(true);
        try {
            if (recordId) {
                await updateApplication(recordId, {
                    "Program Fit": s3.programFit,
                    "Technical Skills": s3.technicalSkills,
                    "Novel Problem Example": s3.novelProblem,
                    "What Makes You an Outlier": s3.outlier,
                    "Portfolio URL": s3.portfolioUrl,
                    "Status": "Section 3",
                });
            }
        } catch (e) {
            console.error("Airtable update failed:", e);
        }
        setSaving(false);
        setStep(4);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleFinalSubmit = async () => {
        setSaving(true);
        try {
            if (recordId) {
                await updateApplication(recordId, {
                    "Essay Prompt": ESSAY_PROMPTS.find(p => p.id === s4.essayPrompt)?.prompt ?? s4.essayPrompt,
                    "Essay": s4.essay,
                    "Video URL": s4.videoUrl,
                    "Status": "Complete",
                    "officiallySubmitted": true,
                    ...(!submitted && { "submittedAt": new Date().toISOString() }),
                }, submitted);
            }
            const isUpdate = submitted;
            sendSlackMessageTracked(
                isUpdate
                    ? `Application updated — ${s1.preferredName || s1.fullName} (${s1.email})`
                    : `Application submitted — ${s1.preferredName || s1.fullName} (${s1.email})`
            );
            if (!isUpdate) sendApplicationConfirmation(s1.email, s1.preferredName || s1.fullName);
            const now = new Date().toISOString();
            if (!submitted) setSubmittedAt(now);
            setSubmitted(true);
            setUpdated(true);
            setTimeout(() => setUpdated(false), 3500);
        } catch (e) {
            console.error("Airtable update failed:", e);
        }
        setSaving(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <div style={{ backgroundColor: "#f7f8fa", minHeight: "100vh" }}>
            <Helmet>
                <meta name="description" content="Apply to Extern Academy — a hands-on startup program for ambitious high school students. Take the first step toward building your own venture." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={PAGE_URL} />
                <meta property="og:title" content="Apply to Extern Academy" />
                <meta property="og:description" content="Ready to build something real? Apply to Extern Academy's startup program for high school students." />
                <meta property="og:image" content={OG_IMAGE} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Apply to Extern Academy" />
                <meta name="twitter:description" content="Ready to build something real? Apply to Extern Academy's startup program for high school students." />
                <meta name="twitter:image" content={OG_IMAGE} />
            </Helmet>

            <div className="max-w-3xl mx-auto px-4 py-12">
                {submitted && <SubmittedBanner submittedAt={submittedAt} updated={updated} />}
                <ProgressBar step={step} />
                <div className="bg-white rounded-2xl shadow-sm p-8">
                    {step === 1 && <Section1 data={s1} set={setS1} onNext={handleSection1} saving={saving} />}
                    {step === 2 && <Section2 data={s2} set={setS2} onNext={handleSection2} onBack={() => setStep(1)} saving={saving} />}
                    {step === 3 && <Section3 data={s3} set={setS3} onNext={handleSection3} onBack={() => setStep(2)} saving={saving} />}
                    {step === 4 && <Section4 data={s4} set={setS4} onSubmit={handleFinalSubmit} onBack={() => setStep(3)} saving={saving} submitted={submitted} />}
                </div>
            </div>

            <FooterComponent />
        </div>
    );
}
